import debug from 'debug';

class Logger {
  constructor() {
    this.debugLoggers = {
      internal: debug('logger:internal'),
      tracked: debug('logger:tracked'),
    };
  }

  log(tag, ...args) {
    this._logWithLevel('info', tag, ...args);
  }

  debug(tag, ...args) {
    this._logWithLevel('debug', tag, ...args);
  }

  info(tag, ...args) {
    this._logWithLevel('info', tag, ...args);
  }

  warn(tag, ...args) {
    this._logWithLevel('warn', tag, ...args);
  }

  error(tag, ...args) {
    this._logWithLevel('error', tag, ...args);
  }

  fatal(tag, ...args) {
    this._logWithLevel('fatal', tag, ...args);
    console.error(tag, ...args);

    // Big fuss to break out the various types of arguments we'll get into a single message and send to Sentry
    try {
      const stringParts = [];
      const dataParts = [];

      args.forEach((arg) => {
        if (typeof arg === 'string' || typeof arg === 'number' || typeof arg === 'boolean') {
          stringParts.push(String(arg));
        } else {
          dataParts.push(arg);
        }
      });

      const message = `[${tag}] ${stringParts.join(' ') || '(no message)'}`;
      if (typeof Sentry !== 'undefined' && Sentry.withScope) {
        Sentry.withScope((scope) => {
          scope.setLevel('fatal');
          scope.setTag('log_tag', tag);
          if (dataParts.length === 1) {
            scope.setExtra('data', dataParts[0]);
          } else if (dataParts.length > 1) {
            scope.setExtra('data', dataParts);
          }
          scope.setExtra('raw_args', args);
          Sentry.captureMessage(message);
        });
      } else if (typeof Sentry !== 'undefined' && Sentry.captureMessage) {
        Sentry.captureMessage(message);
      }
    } catch (internalError) {
      this.debugLoggers.internal('Failed to capture exception:', internalError, { args });
    }
  }

  _logWithLevel(level, tag, ...args) {
    // always log to debug
    if (!this.debugLoggers[`${level}:${tag}`]) this.debugLoggers[`${level}:${tag}`] = debug(`logger:${level}:${tag}`);
    this.debugLoggers[`${level}:${tag}`](...args);

    // Process arguments to create message and data
    let message = '';
    let data = null;
    let stack = null;

    // If single argument
    if (args.length === 1) {
      const arg = args[0];
      if (typeof arg === 'string') {
        message = arg;
      } else if (arg instanceof Error) {
        message = arg.message;
        stack = arg.stack;
        data = { name: arg.name, message: arg.message };
      } else {
        message = String(arg);
        data = arg;
      }
    }
    // Multiple arguments
    else if (args.length > 1) {
      // Build message from non-object arguments
      const messageparts = [];
      const dataparts = [];

      args.forEach((arg) => {
        if (typeof arg === 'string' || typeof arg === 'number' || typeof arg === 'boolean') {
          messageparts.push(String(arg));
        } else if (arg instanceof Error) {
          messageparts.push(arg.message);
          if (!stack) stack = arg.stack;
          dataparts.push({ error: { name: arg.name, message: arg.message } });
        } else {
          dataparts.push(arg);
        }
      });

      message = messageparts.join(' ');

      // Combine all data objects/arrays
      if (dataparts.length === 1) {
        data = dataparts[0];
      } else if (dataparts.length > 1) {
        data = dataparts;
      }
    }

    const logEntry = { id: crypto.randomUUID(), tag, message: message || '(no message)', timestamp: Date.now(), level, data };

    // Only add stack if it exists
    if (stack) logEntry.stack = stack;

    // Dispatch event for LogViewer to capture
    try {
      window.dispatchEvent(new CustomEvent('logger:log', { detail: logEntry }));
    } catch (error) {
      this.debugLoggers.internal('Failed to dispatch log event:', error);
    }
  }

  track(id, value) {
    this.debugLoggers.tracked(id, value);

    try {
      const updateData = { id, value, timestamp: Date.now() };

      // stringify just to be sure there's no browser serialization funny business
      updateData.value = JSON.parse(JSON.stringify(value));

      window.dispatchEvent(new CustomEvent('logger:track', { detail: updateData }));
    } catch (error) {
      // Just log the error and give up - it's only for debugging
      this.debugLoggers.internal(`Failed to track value for ${id}:`, error);
    }
  }
}

// Create singleton instance
const logger = new Logger();

// Export both the logger instance and convenient log functions
export default logger;
export const log = (tag, ...args) => logger.info(tag, ...args);
export const logDebug = (tag, ...args) => logger.debug(tag, ...args);
export const logInfo = (tag, ...args) => logger.info(tag, ...args);
export const logWarn = (tag, ...args) => logger.warn(tag, ...args);
export const logError = (tag, ...args) => logger.error(tag, ...args);
export const logFatal = (tag, ...args) => logger.fatal(tag, ...args);
export const track = (id, value) => logger.track(id, value);
